import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AppShell from './components/AppShell';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import {
  clearStoredAuth,
  isSessionExpired,
  readStoredAuth,
} from './services/api';

const HomePage = lazy(() => import('./pages/HomePage'));
const SemesterPage = lazy(() => import('./pages/SemesterPage'));
const SubjectPage = lazy(() => import('./pages/SubjectPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DepartmentPage = lazy(() => import('./pages/DepartmentPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminUploadPage = lazy(() => import('./pages/AdminUploadPage'));
const AdminFilesPage = lazy(() => import('./pages/AdminFilesPage'));
const AdminManagePage = lazy(() => import('./pages/AdminManagePage'));

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [auth, setAuth] = useState(() => readStoredAuth());

  const isAuthenticated = useMemo(() => Boolean(auth.token), [auth.token]);

  useEffect(() => {
    // Check fixed 30-minute session expiration only if logged in
    const checkSession = () => {
      const stored = readStoredAuth();

      if (auth.token && isSessionExpired()) {
        clearStoredAuth();
        setAuth({ token: '', username: '' });

        if (window.location.pathname.startsWith('/admin')) {
          navigate('/login', {
            replace: true,
            state: { sessionExpired: true },
          });
        }
        return;
      }

      if (stored.token !== auth.token || stored.username !== auth.username) {
        setAuth(stored);
      }
    };

    checkSession();

    // Periodic check every 5 seconds for active admins
    const interval = setInterval(checkSession, 5000);
    window.addEventListener('storage', checkSession);
    window.addEventListener('focus', checkSession);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkSession);
      window.removeEventListener('focus', checkSession);
    };
  }, [auth.token, auth.username, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname.startsWith('/admin') && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  const logout = () => {
    clearStoredAuth();
    setAuth({ token: '', username: '' });
    setMobileSidebarOpen(false);
    navigate('/', { replace: true });
  };

  const isAdminLayout = location.pathname.startsWith('/admin');
  const showSidebar = isAdminLayout || location.pathname === '/';

  return (
    <AppShell
      auth={auth}
      onLogout={logout}
      isAdminLayout={isAdminLayout}
      showSidebar={showSidebar}
      mobileSidebarOpen={mobileSidebarOpen}
      setMobileSidebarOpen={setMobileSidebarOpen}
    >
      <ErrorBoundary>
        <Suspense fallback={<div className="page-loader">Loading…</div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/semester/:id" element={<SemesterPage />} />
            <Route path="/department/:id" element={<DepartmentPage />} />
            <Route path="/subject/:id" element={<SubjectPage />} />
            <Route path="/login" element={<LoginPage setAuth={setAuth} />} />

            <Route
              path="/admin"
              element={(
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminDashboard auth={auth} onLogout={logout} />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/admin/upload"
              element={(
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminUploadPage auth={auth} onLogout={logout} />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/admin/files"
              element={(
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminFilesPage auth={auth} onLogout={logout} />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/admin/manage"
              element={(
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminManagePage auth={auth} onLogout={logout} />
                </ProtectedRoute>
              )}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  );
}

export default App;
